import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { SponsorDto } from "../../../models/sponsor/dto/sponsor.dto";
import { UpdateSponsorInputDto } from "../../../models/sponsor/dto/update-sponsor.dto";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { GET_SPONSOR_QUERY_KEY } from "./useGetSponsor";

export type UpdateSponsorWithLogoInput = {
    sponsorData: Omit<UpdateSponsorInputDto, 'logoUrl'>;
    logoFile?: File;
};

export const useUpdateSponsor = () => {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const { selectedOrganization } = useSelectedOrganization();
    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: UpdateSponsorInputDto | UpdateSponsorWithLogoInput }) => {
            // Check if it's the new format with logo file
            if ('sponsorData' in input) {
                const formData = new FormData();

                // Append all sponsor data fields
                Object.entries(input.sponsorData).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        formData.append(key, value.toString());
                    }
                });

                // Append logo file if provided
                if (input.logoFile) {
                    formData.append('logo', input.logoFile);
                }

                return client.fetchApiWithFormData<SponsorDto>({
                    endpoint: `sponsors/${id}/with-logo`,
                    method: 'PUT',
                    formData
                });
            } else {
                // Legacy format - regular JSON request
                return client.fetchApi<SponsorDto, UpdateSponsorInputDto>({
                    endpoint: `sponsors/${id}`,
                    method: 'PUT',
                    body: input
                });
            }
        },
        onSuccess: (data) => {
            queryClient.setQueryData(GET_SPONSOR_QUERY_KEY(data.id), data);
            queryClient.invalidateQueries({ queryKey: ['sponsors', 'list', selectedOrganization?.id || ''] });
        },
    });
};
